import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface LoadingOverlayProps {
  onLoadingComplete?: () => void;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined' && sessionStorage.getItem('mk_splash_seen')) {
      setIsVisible(false);
      onLoadingComplete?.();
      return;
    }

    const startTime = Date.now();
    const duration = 800;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (newProgress < 100) {
        requestAnimationFrame(updateProgress);
      } else {
        sessionStorage.setItem('mk_splash_seen', '1');
        setIsVisible(false);
        window.scrollTo(0, 0);
        setTimeout(() => {
          onLoadingComplete?.();
        }, 300);
      }
    };

    requestAnimationFrame(updateProgress);

    return () => {
      setIsVisible(false);
    };
  }, [onLoadingComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="fixed inset-0 bg-background flex flex-col items-center justify-center z-[9999]"
        >
          <div className="relative overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-furniture-gold-light/20 to-transparent"
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                ease: "linear",
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="relative"
            >
              <h1 className="font-sans font-bold text-5xl md:text-7xl lg:text-8xl tracking-wider text-black">
                Meubles Karim
              </h1>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, ease: "easeInOut", delay: 0.2 }}
                className="absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-black to-transparent"
              />
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-8 font-sans text-lg font-semibold tracking-wider text-black"
          >
            Le design comme signature.
          </motion.p>

          <div className="mt-12 h-0.5 w-48 overflow-hidden bg-black/15">
            <div
              className="h-full origin-left bg-black transition-[width] duration-75 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingOverlay;
